
import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { compressImage, uploadToStorage } from '@/lib/imageCompression';

interface ImageUploadProps {
  imagePreview: string;
  imageFile: File | null;
  existingImages: string[];
  mode: 'add' | 'edit';
  onImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onOptimizedImageUrl?: (url: string) => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};


const ImageUpload: React.FC<ImageUploadProps> = ({
  imagePreview,
  imageFile,
  existingImages,
  mode,
  onImageChange,
  onOptimizedImageUrl
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: "File Too Large",
        description: `Please select an image smaller than ${formatFileSize(MAX_FILE_SIZE)}.`,
        variant: "destructive",
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File Type",
        description: "Please select a valid image file.",
        variant: "destructive",
      });
      return;
    }

    // Show local preview instantly
    onImageChange(e);

    // Compress & upload in background
    setIsUploading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: "Authentication Error", description: "Please log in to upload images.", variant: "destructive" });
        return;
      }

      const originalSize = file.size;
      const compressed = await compressImage(file);
      const publicUrl = await uploadToStorage(compressed, session.user.id, 'listing-images');

      toast({
        title: "Image Uploaded",
        description: `Compressed from ${formatFileSize(originalSize)} to ${formatFileSize(compressed.size)}`,
      });

      onOptimizedImageUrl?.(publicUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({ title: "Upload Failed", description: "Failed to upload image. Please try again.", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const hasImage = mode === 'edit' ? (existingImages.length > 0 || imageFile) : imageFile;

  return (
    <div className="mb-8">
      <div 
        className="aspect-video rounded-lg overflow-hidden bg-gray-200 border-2 border-dashed border-gray-300 cursor-pointer hover:border-gray-400 transition-colors relative"
        onClick={handleImageClick}
      >
        {imagePreview ? (
          <>
            <img 
              src={imagePreview} 
              alt="Listing preview"
              className="w-full h-full object-cover"
            />
            {isUploading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="text-white text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                  <p className="text-sm">Uploading...</p>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Click to upload image *</p>
            </div>
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        className="hidden"
      />
      <div className="mt-4 space-y-2">
        <Button
          variant="outline"
          onClick={handleImageClick}
          disabled={isUploading}
          className="w-full"
        >
          {isUploading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              {imageFile ? 'Change Image' : mode === 'edit' ? 'Upload New Image' : 'Upload Image *'}
            </>
          )}
        </Button>
        
        <div className="flex items-center text-sm text-gray-500">
          <AlertCircle className="w-4 h-4 mr-1" />
          <span>Maximum file size: {formatFileSize(MAX_FILE_SIZE)}. Images will be automatically compressed.</span>
        </div>
        
        {!hasImage && (
          <p className="text-sm text-red-600">Image is required</p>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;
